import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress
} from '@mui/material';
import { useSupplier } from '../../hooks/useSupplier';
import { useToast } from '@shared/hooks/useToast';
import { Supplier } from '@domain/suppliers/models/Supplier';

interface EditSupplierDialogProps {
  open: boolean;
  onClose: () => void;
  supplier: Supplier | null;
}

interface EditFormData {
  name: string;
  email?: string;
  phone?: string;
  website?: string;
}

const EditSupplierDialog: React.FC<EditSupplierDialogProps> = ({
  open,
  onClose,
  supplier
}) => {
  const { updateSupplier, isUpdating } = useSupplier(supplier?.id ?? '');
  const { success, error } = useToast();
  
  const [formData, setFormData] = useState<EditFormData>({
    name: '',
    email: undefined,
    phone: undefined,
    website: undefined,
  });

  // Update form data when supplier changes
  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        email: supplier.email,
        phone: supplier.phone,
        website: supplier.website,
      });
    }
  }, [supplier]);

  const handleInputChange = (field: keyof EditFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!supplier || !formData.name.trim() || !formData.email?.trim()) {
      return;
    }

    try {
      await updateSupplier({
        id: supplier.id,
        updates: {
          name: formData.name.trim(),
          email: formData.email?.trim(),
          phone: formData.phone?.trim(),
          website: formData.website?.trim(),
        }
      });
      
      success('Supplier updated successfully');
      onClose();
    } catch (err) {
      error('Failed to update supplier');
    }
  };

  const handleClose = () => {
    // Reset form to original values
    if (supplier) {
      setFormData({
        name: supplier.name,
        email: supplier.email,
        phone: supplier.phone,
        website: supplier.website,
      });
    }
    onClose();
  };

  const isFormValid = formData.name.trim() && formData.email?.trim();
  const hasChanges = supplier && (
    formData.name !== supplier.name ||
    formData.email !== supplier.email ||
    formData.phone !== supplier.phone ||
    (formData.website ?? '') !== (supplier.website ?? '')
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Supplier</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label="Supplier Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter supplier name"
            required
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter email address"
            required
          />

          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Enter phone number (optional)"
          />

          <TextField
            fullWidth
            label="Website"
            type="url"
            value={formData.website ?? ''}
            onChange={(e) => handleInputChange('website', e.target.value)}
            placeholder="https://supplier.com (optional)"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!isFormValid || !hasChanges || isUpdating}
          startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
        >
          {isUpdating ? 'Updating...' : 'Update Supplier'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSupplierDialog; 